import { useState } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { Lock, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { UpgradeModal } from './UpgradeModal';

/**
 * Wraps premium-only content with an upgrade prompt for free users.
 * Usage: <PremiumGate feature="ai-exam-prep"><ExamPrepContent /></PremiumGate>
 */
export const PremiumGate = ({ feature, children, fallback = null }) => {
  const { canAccess } = useSubscription();
  const [showUpgrade, setShowUpgrade] = useState(false);

  if (canAccess(feature)) {
    return children;
  }

  if (fallback) return fallback;

  return (
    <>
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center" data-testid={`premium-gate-${feature}`}>
        <div className="w-16 h-16  bg-gradient-to-br from-purple-500/20 to-indigo-500/20 flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-purple-400" />
        </div>
        <h3 className="text-lg font-bold text-[var(--isp-heading)] font-heading mb-2">Premium Feature</h3>
        <p className="text-sm text-[var(--isp-body)] max-w-sm mb-6">
          Upgrade your plan to unlock this feature and supercharge your study workflow.
        </p>
        <Button
          onClick={() => setShowUpgrade(true)}
          className="rounded-xl gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:opacity-90"
          data-testid={`upgrade-btn-${feature}`}
        >
          <Crown className="w-4 h-4" /> Upgrade to Premium
        </Button>
      </div>
      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />
    </>
  );
};

/**
 * Small inline badge for premium features in nav/cards
 */
export const PremiumBadge = ({ className = '' }) => (
  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-400 ${className}`} data-testid="premium-badge">
    <Crown className="w-2.5 h-2.5" /> PRO
  </span>
);
